import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useSessionDataWithRovoExperiment } from '@confluence/session-data';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant';

interface LoomScriptReadTimeContextProps {
	isLoomScriptReadTimeExtensionEnabled: boolean;
	isLoomScriptReadTimeButtonNudgeShown: boolean;
	isLoomScriptReadTimeSpotlightShown: boolean;
	updateLoomScriptReadTimeEligibilityForPage: (
		contentNode: any,
		readTime: number | null,
		isPageWithLoomVideos: boolean | undefined,
	) => void;
	hideLoomScriptReadTimeButtonNudge: () => void;
	dismissLoomScriptReadTimeSpotlight: () => void;
}

const LoomScriptReadTimeContext = createContext<LoomScriptReadTimeContextProps>({
	isLoomScriptReadTimeExtensionEnabled: false,
	isLoomScriptReadTimeButtonNudgeShown: false,
	isLoomScriptReadTimeSpotlightShown: false,
	updateLoomScriptReadTimeEligibilityForPage: () => {},
	hideLoomScriptReadTimeButtonNudge: () => {},
	dismissLoomScriptReadTimeSpotlight: () => {},
});

export const useLoomScriptReadTimeContext = () => useContext(LoomScriptReadTimeContext);

interface LoomScriptReadTimeContextProviderProps {
	isLoomAIScriptExperimentEnabled: boolean;
	children: React.ReactNode;
}

/**
 * Provides a context for the Loom Script Read Time Summary Video section, Nudge, and Spotlight.
 *
 * Summary Video section is shown if the page meets the basic criteria
 * Nudge is shown if the page also satisfies a minimum read time, and the spotlight has not already been dismissed
 * Nudge is hidden when it is clicked on, and the spotlight shown in the dropdown
 * When the spotlight is dismissed, mark it as dismissed in local storage, so the nudge will not show again
 */
const LoomScriptReadTimeEnabledContextProvider = ({
	children,
}: LoomScriptReadTimeContextProviderProps) => {
	const isLivePage = useIsCurrentPageLive();
	const { entryPointVariant } = useLoomEntryPointVariant();
	const { isRovoEnabled, userId } = useSessionDataWithRovoExperiment();

	const [isLoomScriptReadTimeExtensionEnabled, setIsLoomScriptReadTimeExtensionEnabled] =
		useState(false);

	const [isLoomScriptReadTimeButtonNudgeShown, setIsLoomScriptReadTimeButtonNudgeShown] =
		useState(false);

	const [isLoomScriptReadTimeSpotlightShown, setIsLoomScriptReadTimeSpotlightShown] =
		useState(false);

	const [isMinimumReadTimeMet, setIsMinimumReadTimeMet] = useState(false);

	useEffect(() => {
		if (isLoomScriptReadTimeExtensionEnabled && isMinimumReadTimeMet) {
			const hasDismissedLoomScriptReadTimeButtonNudge = localStorage.getItemAsBoolean(
				PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_SCRIPT_READTIME_BUTTON_NUDGE_DISMISSED,
			);
			// show the spotlight and nudge if we are eligible and the spotlight has not been dismissed
			setIsLoomScriptReadTimeSpotlightShown(!hasDismissedLoomScriptReadTimeButtonNudge);
			setIsLoomScriptReadTimeButtonNudgeShown(!hasDismissedLoomScriptReadTimeButtonNudge);
		} else {
			setIsLoomScriptReadTimeSpotlightShown(false);
			setIsLoomScriptReadTimeButtonNudgeShown(false);
		}
	}, [isLoomScriptReadTimeExtensionEnabled, isMinimumReadTimeMet]);

	const updateLoomScriptReadTimeEligibilityForPage = useCallback(
		(contentNode: any, readTime: number | null, isPageWithLoomVideos: boolean | undefined) => {
			if (!contentNode || !isRovoEnabled || isPageWithLoomVideos || readTime === null) {
				setIsLoomScriptReadTimeExtensionEnabled(false);
				return;
			}

			setIsMinimumReadTimeMet(readTime >= 7);

			const contentType = contentNode?.type;
			const isContentCreator =
				'accountId' in (contentNode?.history?.createdBy ?? {}) &&
				contentNode?.history?.createdBy?.accountId === userId;

			const canEdit = contentNode?.operations?.some(
				(op: { operation: string; targetType: any }) =>
					op?.operation === 'update' && op?.targetType === contentType,
			);

			setIsLoomScriptReadTimeExtensionEnabled(
				isContentCreator &&
					contentType === 'page' &&
					(entryPointVariant === 'CO_USE' ||
						entryPointVariant === 'CROSS_FLOW' ||
						entryPointVariant === 'CROSS_JOIN') &&
					canEdit &&
					!isLivePage,
			);
		},
		[isRovoEnabled, userId, entryPointVariant, isLivePage],
	);

	const contextValue = useMemo(
		() => ({
			isLoomScriptReadTimeExtensionEnabled,
			isLoomScriptReadTimeButtonNudgeShown,
			isLoomScriptReadTimeSpotlightShown,
			updateLoomScriptReadTimeEligibilityForPage,
			hideLoomScriptReadTimeButtonNudge: () => setIsLoomScriptReadTimeButtonNudgeShown(false),
			dismissLoomScriptReadTimeSpotlight: () => {
				setIsLoomScriptReadTimeSpotlightShown(false);
				localStorage.setItem(
					PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_SCRIPT_READTIME_BUTTON_NUDGE_DISMISSED,
					'true',
				);
			},
		}),
		[
			isLoomScriptReadTimeButtonNudgeShown,
			isLoomScriptReadTimeSpotlightShown,
			isLoomScriptReadTimeExtensionEnabled,
			updateLoomScriptReadTimeEligibilityForPage,
		],
	);

	return (
		<LoomScriptReadTimeContext.Provider value={contextValue}>
			{children}
		</LoomScriptReadTimeContext.Provider>
	);
};

export const LoomScriptReadTimeContextProvider = (props: LoomScriptReadTimeContextProviderProps) =>
	props.isLoomAIScriptExperimentEnabled ? (
		<LoomScriptReadTimeEnabledContextProvider {...props} />
	) : (
		props.children
	);
